import Container from '@/components/Container';
import DefaultContentTemplate from '@/components/DefaultContentTemplate';
import DefaultContentTemplateMedia from '@/components/DefaultContentTemplate/Media';
import Divider from '@/components/Divider';
import PageHeader from '@/components/PageHeader';
import TitleWithActions from '@/components/TitleWithActions';
import { trackGAEvent } from '@/lib/ga-custom-events/dispatcher';
import { fetchResourceHubPage } from '@/services/resource-hub/resource-hub.service';
import { fetchRelatedContent, fetchResource } from '@/services/resource-hub/resource.service';
import { getAuthors } from '@/services/resource-hub/utils';
import { CONTENT_TYPES, getResourceContentType } from '@/utils/content-type';
import {
  ComponentCommonMedia,
  Enum_Like_Type,
  GENERATED,
  PodcastListeningTime,
  Resource,
  ResourceHubPage,
  SubTraitRelationResponseCollection,
  UploadFile,
} from '@edwin-group/cj-graphql';
import { format } from 'date-fns';
import { useEffect, useMemo, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
  createPodcastListeningTime,
  findListeningTime,
  updatePodcastListeningTime,
} from '@/services/podcast/podcast.service';
import { getAuthentication } from '@/hooks/useAuthentication';

const arrowPodcastImg = '/card/arrow-podcast.png';
const arrowContentImg = '/card/arrow-content.png';

export type ListeningTimeRefProps = {
  documentId: string | null;
  id: string | null;
  listeningTime: number;
  duration: number;
};

export default function ResourcePage() {
  const [resource, setResource] = useState<Resource | null>(null);
  const [resourceHubPage, setResourceHubPage] = useState<ResourceHubPage | null>();
  const [relatedContent, setRelatedContent] = useState<Resource[] | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const { user } = getAuthentication();

  const lastSentTimeRef = useRef<ListeningTimeRefProps>({ documentId: null, id: null, listeningTime: 0, duration: 0 }); // Ref to store last sent time

  const isPodcast = useMemo(() => {
    const contentType = getResourceContentType(resource?.attributes?.resourceType?.data?.attributes?.type);
    return contentType === CONTENT_TYPES.PODCAST;
  }, [resource?.attributes?.resourceType?.data?.attributes?.type]);

  const { slug } = useParams();

  useEffect(() => {
    (async () => {
      if (!slug) return;
      setLoading(true);
      const [resourceResult, relatedContentResult, page] = await Promise.all([
        fetchResource(slug),
        fetchRelatedContent(slug),
        fetchResourceHubPage(),
      ]);

      const listeningTimeResult = await findListeningTime(resourceResult.resource?.documentId || '');

      const progress = Math.ceil(
        (listeningTimeResult?.listeningTime?.listeningTime / listeningTimeResult?.listeningTime?.duration) * 100,
      );
      const isDone = progress >= 90;

      if (listeningTimeResult.listeningTime) {
        lastSentTimeRef.current = listeningTimeResult.listeningTime;
        if (isDone) {
          lastSentTimeRef.current!.listeningTime = 0;
          lastSentTimeRef.current = { ...lastSentTimeRef.current };
        }
      }

      const resourceData = resourceResult?.resource;
      setResourceHubPage(page.page as unknown as ResourceHubPage);
      setResource(resourceData as unknown as Resource);
      setRelatedContent(relatedContentResult.resources || []);
      setLoading(false);
      trackGAEvent('resource_opened', {
        resource_document_id: resourceData?.documentId ?? '',
        resource_name: resourceData?.attributes?.title ?? '',
        resource_type: resourceData?.attributes?.resourceType?.data?.attributes?.type ?? '',
      });

      if (resourceData) {
        // const topic = resourceData.attributes?.themes?.data[0]?.attributes?.topic?.data?.attributes;
        // const topicId = resourceData.attributes?.themes?.data[0]?.attributes?.topic?.data?.id;
        // const themes = resourceData.attributes?.themes?.data?.map((theme) => `${theme.attributes?.theme}`).join(', ');
        // const themeId = resourceData.attributes?.themes?.data?.map((theme) => `${theme.id}`).join(', ');
        // trackResource({
        //   topic_id: topicId || '',
        //   topic_name: topic?.topic || '',
        //   theme_id: themeId || '',
        //   theme_name: themes || '',
        // });
      }
    })();
  }, [slug]);

  //   useEffect(() => {
  //     return () => {
  //       clearResourceDataLayer();
  //     };
  //   }, []);

  const handleTimeUpdate = async (audio: HTMLAudioElement) => {
    const currentTime = Math.floor(audio.currentTime);
    const duration = Math.floor(audio.duration);
    if (!lastSentTimeRef.current.id) {
      const { data, error } = await createPodcastListeningTime({
        duration: duration,
        listeningTime: currentTime,
        podcast: resource?.documentId || '',
        staff: user?.documentId,
      });

      if (error) {
        console.error('Error creating podcast listening time', error);
        return;
      }

      if (data) {
        lastSentTimeRef.current = data.createPodcastListeningTime as PodcastListeningTime;
      }
      return;
    }
    if (lastSentTimeRef?.current?.documentId) {
      await updatePodcastListeningTime(lastSentTimeRef?.current?.documentId, {
        listeningTime: currentTime,
      });
    }

    return;
  };

  const renderResourceHeader = () => {
    const authors = getAuthors(resource as Resource);
    return (
      <div>
        <Container>
          <div className="col-span-12 lg:col-start-3 lg:col-end-11">
            <PageHeader title="Resource Hub" type="none" />
          </div>
        </Container>
        <Container>
          <div className="col-span-12 lg:col-start-3 lg:col-end-11">
            <DefaultContentTemplateMedia
              arrowImg={isPodcast ? arrowPodcastImg : arrowContentImg}
              className="!mb-sh-15 mt-sh-30"
              component={
                {
                  id: '1',
                  media: {
                    data: {
                      attributes: {
                        url: resource?.attributes?.cover.data?.attributes?.url || '',
                        alternativeText: resource?.attributes?.cover.data?.attributes?.alternativeText || '',
                      } as UploadFile,
                    },
                  },
                } as ComponentCommonMedia
              }
            />

            {resource?.documentId ? (
              <TitleWithActions
                title={resource!.attributes!.title}
                entity={resource}
                category={GENERATED.Enum_Bookmark_Category.Resource}
                type={Enum_Like_Type.Resource}
                resourceTypeId={resource?.attributes?.resourceType?.data?.id}
                entityId={resource!.documentId}
              />
            ) : null}

            <p className="my-sh-15 text-sh-18-24">{resource?.attributes?.subHeader}</p>

            <div className="font-cadiz text-sh-14-16 text-sh-grey-4e">
              {resource?.attributes?.publicationDate ? (
                <span>{format(new Date(resource?.attributes?.publicationDate), 'dd.MM.yyyy')?.toUpperCase()}</span>
              ) : null}
              {authors ? (
                <span>
                  By <strong>{authors}</strong>
                </span>
              ) : null}
            </div>

            <Divider />
          </div>
        </Container>
      </div>
    );
  };

  return (
    <>
      {!loading && renderResourceHeader()}
      <Container className="min-h-40">
        <div className="col-span-12 lg:col-start-3 lg:col-end-11">
          <DefaultContentTemplate
            loading={loading}
            content={resource?.attributes?.DefaultContentTemplate}
            handleTimeUpdate={handleTimeUpdate}
            listeningTime={lastSentTimeRef.current}
            onPlayAudio={() => {
              if (isPodcast) {
                trackGAEvent('podcast_played', {
                  podcast_document_id: resource?.documentId ?? '',
                  podcast_name: resource?.attributes?.title ?? '',
                });
              }
            }}
            traitTags={
              (resource?.attributes?.subTraits as unknown as SubTraitRelationResponseCollection)?.data
                .map((subtrait) => {
                  return subtrait?.attributes?.trait?.data?.attributes;
                })
                .filter((t): t is NonNullable<typeof t> => !!t) || []
            }
            resourcesRelated={relatedContent || []}
            downloadableContentTitle={resourceHubPage?.contentTemplateLabels?.downloadableContentLabel || undefined}
            downloadableContentIcon={resourceHubPage?.contentTemplateIcons?.downloadableContentIcon?.url || undefined}
          />
        </div>
      </Container>
    </>
  );
}
